import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { fmt } from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/pricingSummary.utils';
import {
  PricingRow,
  DiscountText,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer.styles';

import type { ChargeSectionProps } from './ChargeSection.types';

export const ChargeSection = ({ title, breakdown, showShipping, showTaxes = true }: ChargeSectionProps) => {
  if (breakdown.items.length === 0 && !showShipping) return null;

  return (
    <Stack spacing={1.25} useFlexGap>
      <Typography
        className="OrderSummaryTitle"
        sx={{
          fontFamily: 'Agrandir',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '100%',
        }}
      >
        {title}
      </Typography>

      {breakdown.items.map((item, i) => (
        <Box key={i}>
          <PricingRow>
            <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px' }}>
              {item.name}
            </Typography>
            <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '14px' }}>
              {fmt(item.amount)}
            </Typography>
          </PricingRow>
          {item.children.map((child, j) => (
            <PricingRow key={j} sx={{ pl: '10px', mt: '2px' }}>
              <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px' }}>
                &bull;&nbsp;{child.name}
                {child.typeSuffix ? ` - ${child.typeSuffix}` : ''}
                {(child.quantity ?? 1) > 1 ? ` x ${child.quantity}` : ''}
              </Typography>
              <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '14px' }}>
                {fmt(child.amount)}
              </Typography>
            </PricingRow>
          ))}
        </Box>
      ))}

      {breakdown.discounts.length > 0 && (
        <Box>
          <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px' }}>
            Discounts
          </Typography>
          {breakdown.discounts.map((d, i) => (
            <PricingRow key={i} sx={{ pl: '10px', mt: '2px' }}>
              <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px' }}>
                &bull;&nbsp;{d.name}
              </Typography>
              <DiscountText sx={{ fontSize: '14px' }}>-{fmt(Math.abs(d.amount))}</DiscountText>
            </PricingRow>
          ))}
        </Box>
      )}

      {showShipping && (
        <PricingRow>
          <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px' }}>
            Installation Fee
          </Typography>
          <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px' }}>
            {fmt(0)}
          </Typography>
        </PricingRow>
      )}

      {showTaxes && breakdown.taxLines.length > 0 && (
        <Box>
          <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px' }}>
            Estimated Taxes
          </Typography>
          {breakdown.taxLines.map((tax, i) => (
            <PricingRow key={i} sx={{ pl: '10px', mt: '2px' }}>
              <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px' }}>
                &bull;&nbsp;{tax.name}
              </Typography>
              <Typography className="OrderSummaryText" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '14px' }}>
                {fmt(tax.amount)}
              </Typography>
            </PricingRow>
          ))}
        </Box>
      )}
    </Stack>
  );
};
